import { useEffect, useRef } from "react";
import { prefersReducedMotion } from "@/components/system";

/* ═══════════════════════════════════════════════════════════════════════════
   HexField.

   A hexagonal mesh drawn on a 2D canvas, sitting in the hero's depth stack
   between the bloom and the wordmark. Three things happen on it:

     base    a faint lattice, painted once to an offscreen canvas and blitted
             every frame — the part that never changes is never re-stroked.
     sweep   a soft diagonal band of light that crosses the field on a slow
             period, so the mesh is alive on a page with no cursor at all.
     touch   cells within reach of the pointer brighten and fill, falling off
             with distance. The pointer is followed through a lerp, so the
             glow settles behind the cursor rather than snapping to it.

   Everything is painted from --line and --bone. It is a lit surface, not a
   colour: a mesh that promises neither a click nor a number stays neutral
   under the two-colour rule.

   Costs are bounded on purpose. Devices with no fine pointer skip the touch
   pass, phones draw at a lower cadence with a smaller cell, and reduced
   motion paints the base lattice once and never starts the loop.
   ═══════════════════════════════════════════════════════════════════════════ */

const LINE = "139, 120, 98";
const BONE = "250, 246, 239";

export function HexField({ className = "" }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d", { alpha: true });
    if (!ctx) return undefined;

    const reduced = prefersReducedMotion();
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const phone = window.matchMedia("(max-width: 767px)").matches;

    const R = phone ? 22 : 28;               // circumradius
    const REACH = phone ? 0 : 250;           // pointer falloff radius
    const FRAME_MS = phone ? 50 : 16;        // ~20fps on phones, ~60 elsewhere
    const PERIOD = 15000;                    // sweep crossing time

    let w = 0, h = 0, dpr = 1;
    let cells = [];
    let base = null;
    let raf = 0;
    let last = 0;
    const target = { x: -1e4, y: -1e4 };
    const cur = { x: -1e4, y: -1e4 };

    const hexPath = (c, x, y, r) => {
      c.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 6;
        const px = x + r * Math.cos(a);
        const py = y + r * Math.sin(a);
        if (i) c.lineTo(px, py); else c.moveTo(px, py);
      }
      c.closePath();
    };

    const layout = () => {
      const rect = canvas.getBoundingClientRect();
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Pointy-top lattice, overscanned by one cell so the edges never show.
      const dx = Math.sqrt(3) * R;
      const dy = 1.5 * R;
      cells = [];
      for (let row = 0, y = -R; y < h + R * 2; row++, y += dy) {
        const off = row & 1 ? dx / 2 : 0;
        for (let x = -R + off; x < w + R * 2; x += dx) cells.push({ x, y });
      }

      // The base lattice, once.
      base = document.createElement("canvas");
      base.width = canvas.width;
      base.height = canvas.height;
      const b = base.getContext("2d");
      b.setTransform(dpr, 0, 0, dpr, 0, 0);
      b.lineWidth = 1;
      b.strokeStyle = `rgba(${LINE}, 0.062)`;
      for (const c of cells) { hexPath(b, c.x, c.y, R - 1.5); b.stroke(); }

      if (reduced) draw(0, true);
    };

    const draw = (now, still = false) => {
      ctx.clearRect(0, 0, w, h);
      if (base) ctx.drawImage(base, 0, 0, w, h);
      if (still) return;

      // Sweep: a band along the x + 0.6y diagonal, travelling corner to corner.
      const phase = (now % PERIOD) / PERIOD;
      const span = w + h * 0.6;
      const bandAt = phase * (span + 600) - 300;
      const sigma = 150;

      cur.x += (target.x - cur.x) * 0.09;
      cur.y += (target.y - cur.y) * 0.09;

      ctx.lineWidth = 1;
      for (const c of cells) {
        const along = c.x + c.y * 0.6 - bandAt;
        const band = Math.exp(-(along * along) / (2 * sigma * sigma));

        let near = 0;
        if (fine && REACH) {
          const d = Math.hypot(c.x - cur.x, c.y - cur.y);
          near = d < REACH ? 1 - d / REACH : 0;
        }

        const lit = band * 0.17 + near * near * 0.55;
        if (lit < 0.015) continue;

        hexPath(ctx, c.x, c.y, R - 1.5);
        ctx.strokeStyle = `rgba(${LINE}, ${lit})`;
        ctx.stroke();
        if (near > 0.3) {
          ctx.fillStyle = `rgba(${BONE}, ${(near - 0.3) * 0.11})`;
          ctx.fill();
        }
      }
    };

    const loop = (now) => {
      raf = requestAnimationFrame(loop);
      if (document.hidden || now - last < FRAME_MS) return;
      last = now;
      draw(now);
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      target.x = e.clientX - rect.left;
      target.y = e.clientY - rect.top;
    };
    const onLeave = () => { target.x = -1e4; target.y = -1e4; };

    layout();
    const ro = new ResizeObserver(layout);
    ro.observe(canvas);

    if (!reduced) {
      if (fine) {
        window.addEventListener("pointermove", onMove, { passive: true });
        document.documentElement.addEventListener("pointerleave", onLeave);
      }
      raf = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener("pointermove", onMove);
      document.documentElement.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return <canvas ref={ref} aria-hidden="true" className={`hero-hex ${className}`} />;
}

export default HexField;
